import type { InputChangeEventDetails, InputProps, ToggleProps } from '@base-ui/react';

import { type HTMLInputTypeAttribute, type MouseEvent, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useIntl } from '~/hooks/useIntl';
import { colors } from '~/styles/contracts.css';

/**
 * Control the input value internally when the consumer is using the component uncontrolled.
 * We need to control the input so that we can keep track of input length etc.
 *
 * If the consumer already controls the value, this hook will be a noop:
 * the internal value will not be kept in sync with the external value.
 */
export const useControlledValue = ({
  defaultValue,
  onValueChange: externalOnValueChange,
  value: externalValue,
}: {
  defaultValue?: InputProps['defaultValue'];
  onValueChange?: ClearableInputOnValueChange;
  value?: InputProps['value'];
}) => {
  const [internalValue, setInternalValue] = useState(defaultValue ?? '');
  const internalOnValueChange: ClearableInputOnValueChange = useCallback((value) => setInternalValue(value), []);

  const value = externalValue ?? internalValue;
  const onValueChange = externalOnValueChange ?? internalOnValueChange;

  return { value, onValueChange };
};

/**
 * Focus the input field when the input wrapper is clicked.
 */
export const useInputFocusHandler = () => {
  const inputRef = useRef<HTMLInputElement>(null);

  const onClick = useCallback((e: MouseEvent<HTMLElement>) => {
    if (inputRef.current !== null && e.target !== inputRef.current && inputRef.current?.dataset.focused !== '') {
      inputRef.current.focus();

      const length = inputRef.current.value.length;
      try {
        inputRef.current.setSelectionRange(length, length);
      } catch {
        /* Not all input types support selection range */
      }
    }
  }, []);
  const onMouseDown = useCallback((e: MouseEvent<HTMLElement>) => {
    if (e.target !== inputRef.current) e.preventDefault();
  }, []);

  const inputWrapperProps = useMemo(
    () => ({
      onClick,
      onMouseDown,
    }),
    [onClick, onMouseDown],
  );

  return { inputRef, inputWrapperProps };
};

// Custom `eventDetails` because BaseUI doesn't have native clear functionality
export type ClearableInputOnValueChange = (value: string, eventDetails: InputChangeEventDetails | { reason: 'clear' }) => void;

export const useClearInputHandler = ({ onValueChange }: { onValueChange?: ClearableInputOnValueChange }) => {
  const onClear = useCallback(() => onValueChange?.('', { reason: 'clear' }), [onValueChange]);

  const clearButtonProps = useMemo(
    () =>
      ({
        onClick: onClear,
        'aria-hidden': true,
        tabIndex: -1,
      }) as const,
    [onClear],
  );

  return { clearButtonProps };
};

export const usePasswordInputHandler = ({ type }: { type?: HTMLInputTypeAttribute }) => {
  const [isPasswordRevealed, setIsPasswordRevealed] = useState(false);
  const inputType = isPasswordRevealed ? 'text' : type;

  const onTogglePassword: ToggleProps<'revealPassword'>['onPressedChange'] = useCallback((pressed) => setIsPasswordRevealed(pressed), []);

  const { t } = useTranslation(['common']);

  const revealPasswordButtonProps = useMemo<ToggleProps<'revealPassword'>>(
    () => ({
      'aria-label': t('common:actions.revealPassword'),
      value: 'revealPassword',
      pressed: isPasswordRevealed,
      onPressedChange: onTogglePassword,
    }),
    [isPasswordRevealed, onTogglePassword, t],
  );

  return { inputType, isPasswordRevealed, revealPasswordButtonProps };
};

export const useMaxInputLengthHandler = ({ maxLength, value }: { maxLength?: number; value: InputProps['value'] }) => {
  const lengthSuffixRef = useRef<HTMLSpanElement>(null);
  const { segmentGraphemes } = useIntl();

  const valueAsString = Array.isArray(value) ? value.join(',') : `${value}`;
  const currentLength = [...segmentGraphemes(valueAsString)].length;

  const limitValueLength = useCallback(
    (value: string) => {
      if (maxLength === undefined) return value;

      const graphemes = [...segmentGraphemes(value)].map((g) => g.segment);

      if (graphemes.length <= maxLength) return value;

      const limitedValue = graphemes.slice(0, maxLength).join('');

      lengthSuffixRef.current?.animate(
        [{ transform: 'scale(1)' }, { transform: 'scale(1.15)', color: colors.foreground.strong }, { transform: 'scale(1)' }],
        { duration: 200, easing: 'ease-out' },
      );

      return limitedValue;
    },
    [maxLength, segmentGraphemes],
  );

  return { currentLength, lengthSuffixRef, limitValueLength: maxLength !== undefined ? limitValueLength : undefined };
};
